import React, { useCallback, useMemo, useState } from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import { ToastContext, type ToastOptions } from './use-toast';

type ToastItem = Required<Pick<ToastOptions, 'title' | 'description' | 'variant' | 'duration'>> & {
  id: string;
};

const DEFAULT_DURATION = 4000;

export const AppToasterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id?: string) => {
    if (!id) {
      setToasts([]);
      return;
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = Math.random().toString(36).slice(2);
      const item: ToastItem = {
        id,
        title: options.title ?? '',
        description: options.description ?? '',
        variant: options.variant ?? 'default',
        duration: options.duration ?? DEFAULT_DURATION,
      };
      setToasts((prev) => [...prev, item]);
      return id;
    },
    []
  );

  const ctxValue = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={ctxValue}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => (
          <RadixToast.Root
            key={t.id}
            duration={t.duration}
            className={
              [
                'group pointer-events-auto relative w-80 rounded-md border p-4 pr-10 shadow-lg',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-80 data-[state=open]:fade-in-0',
                'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
                'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-all',
                'data-[swipe=end]:animate-out data-[swipe=end]:slide-out-to-right-full',
                t.variant === 'destructive'
                  ? 'border-destructive/30 bg-destructive text-destructive-foreground'
                  : 'bg-background border-border text-foreground'
              ].join(' ')
            }
            onOpenChange={(open) => {
              if (!open) dismiss(t.id);
            }}
          >
            <div className="flex flex-col gap-1">
              {t.title ? (
                <RadixToast.Title className="text-sm font-semibold">{t.title}</RadixToast.Title>
              ) : null}
              {t.description ? (
                <RadixToast.Description className="text-sm opacity-90">
                  {t.description}
                </RadixToast.Description>
              ) : null}
            </div>
            <RadixToast.Close
              aria-label="Close"
              className={
                [
                  'absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded',
                  'opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring',
                  t.variant === 'destructive'
                    ? 'hover:bg-destructive/20'
                    : 'hover:bg-accent'
                ].join(' ')
              }
              onClick={() => dismiss(t.id)}
            >
              <span className="sr-only">Close</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                <path fillRule="evenodd" d="M10 8.586l4.95-4.95 1.414 1.415L11.414 10l4.95 4.95-1.414 1.414L10 11.414l-4.95 4.95-1.414-1.414L8.586 10l-4.95-4.95L5.05 3.636 10 8.586z" clipRule="evenodd" />
              </svg>
            </RadixToast.Close>
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport
          className="fixed top-4 right-4 z-[100] flex max-h-screen w-96 flex-col gap-2 outline-none"
        />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
};
